import { getMowerRecommendation, getSpreaderRecommendation, type MowerOption } from '@/data/lawn-size-recommendations'

export interface LawnSizeInput {
  measurementMethod: 'map_draw' | 'manual_rectangle' | 'manual_irregular'
  totalAreaSqFt: number
  // Perimeter / edging length in feet. Optional so older callers and shared
  // deep links (which only carry area) still work.
  totalPerimeterFt?: number
  polygon?: Array<{ lat: number; lng: number }>
  // Total vertex count across all drawn lawn-area polygons (map_draw only).
  // Passed through to LawnSizeResult so the results component can show a
  // trace-confidence cue. Absent for manual paths.
  vertexCount?: number
}

// Area unit toggle shared by the live readout and the result hero.
export type AreaUnit = 'sqft' | 'acres' | 'sqm'

export const LAWN_SIZE_RATE_ASSUMPTIONS = {
  grassSeedLbsPer1000SqFt: 5,
  grassSeedBagSizeLbs: 25,
  fertilizerProductLbsPer1000SqFt: 3,
  fertilizerBagSizeLbs: 50,
} as const

const SQ_FT_PER_ACRE = 43560
const SQ_M_PER_SQ_FT = 0.092903

// Reformat a square-foot area into the selected unit, returning a display
// string (value + unit label). sq ft stays the default everywhere.
export function formatArea(sqFt: number, unit: AreaUnit): string {
  switch (unit) {
    case 'acres':
      return `${(sqFt / SQ_FT_PER_ACRE).toLocaleString(undefined, { maximumFractionDigits: 3 })} acres`
    case 'sqm':
      return `${Math.round(sqFt * SQ_M_PER_SQ_FT).toLocaleString()} m²`
    case 'sqft':
    default:
      return `${Math.round(sqFt).toLocaleString()} sq ft`
  }
}

// Net lawn area = gross traced area minus the sum of excluded areas (house,
// driveway, beds). Clamped at 0 so a fully-excluded property never goes
// negative. Pure + testable; the map component threads its exclusion totals
// through here before reporting the result.
export function computeNetArea(grossSqFt: number, excludedSqFt: number): number {
  return Math.max(0, grossSqFt - excludedSqFt)
}

// ============================================================================
// Per-edge distance helper
// ----------------------------------------------------------------------------
// distanceFtBetween returns the great-circle distance between two lat/lng
// points in FEET. Pure (no google.maps dependency) so it is unit-testable in
// jest and drives the live per-edge length labels while a user traces. Uses the
// haversine formula on the WGS84 mean Earth radius, which is plenty accurate at
// residential-lawn scale (sub-foot error over typical edge lengths).
// ============================================================================

// Mean Earth radius in meters (WGS84). 1 meter = 3.280839895 feet.
const EARTH_RADIUS_M = 6371008.8
const FT_PER_M = 3.280839895

const toRad = (deg: number): number => (deg * Math.PI) / 180

/**
 * Great-circle distance between two coordinates, returned in feet.
 * NO google.maps dependency — safe to import in jest.
 */
export function distanceFtBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)

  const sinDLat = Math.sin(dLat / 2)
  const sinDLng = Math.sin(dLng / 2)
  const h =
    sinDLat * sinDLat +
    Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))

  return EARTH_RADIUS_M * c * FT_PER_M
}

// ============================================================================
// Trace confidence assessment
// ============================================================================

export interface TraceConfidence {
  level: 'high' | 'medium' | 'low'
  message?: string
}

/**
 * Assess how reliable a map-drawn polygon trace is based on vertex count and
 * total area. Returns a confidence level + optional guidance message.
 *
 * Thresholds (tuned for residential lawns):
 *   low    : vertexCount < 3 (degenerate) OR areaSqFt < 200 (suspiciously tiny)
 *   medium : vertexCount 3-4 (basic rectangle approximation)
 *   high   : vertexCount >= 5 and area >= 200 (enough corners for a real outline)
 */
export function assessTraceConfidence({ vertexCount, areaSqFt }: {
  vertexCount: number
  areaSqFt: number
}): TraceConfidence {
  if (vertexCount < 3 || areaSqFt < 200) {
    return {
      level: 'low',
      message: 'This looks small for a lawn. Did you finish tracing your outline?',
    }
  }
  if (vertexCount <= 4) {
    return {
      level: 'medium',
      message: 'A 3-4 point trace is a rough estimate. Add corners along curves for a tighter number.',
    }
  }
  return { level: 'high' }
}

// ============================================================================
// Self-intersection detection
// ----------------------------------------------------------------------------
// A traced outline whose edges cross (a "bowtie" / figure-8) produces a wrong
// area and reads as broken. polygonSelfIntersects checks whether any two
// NON-ADJACENT edges of the polygon (treated as closed: last point connects
// back to first) cross. Pure 2D segment intersection on lat/lng, which is fine
// at lawn scale. NO google.maps dependency, so it is unit-testable in jest.
// ============================================================================

interface Point2D {
  lat: number
  lng: number
}

// Orientation of the ordered triplet (p, q, r):
//   0 -> collinear, positive -> counter-clockwise, negative -> clockwise.
const orientation = (p: Point2D, q: Point2D, r: Point2D): number =>
  (q.lng - p.lng) * (r.lat - q.lat) - (q.lat - p.lat) * (r.lng - q.lng)

// Is point q on segment p-r, assuming the three are collinear?
const onSegment = (p: Point2D, q: Point2D, r: Point2D): boolean =>
  q.lat <= Math.max(p.lat, r.lat) && q.lat >= Math.min(p.lat, r.lat) &&
  q.lng <= Math.max(p.lng, r.lng) && q.lng >= Math.min(p.lng, r.lng)

// Standard 2D segment intersection (handles the collinear-overlap cases).
const segmentsIntersect = (p1: Point2D, p2: Point2D, p3: Point2D, p4: Point2D): boolean => {
  const o1 = orientation(p1, p2, p3)
  const o2 = orientation(p1, p2, p4)
  const o3 = orientation(p3, p4, p1)
  const o4 = orientation(p3, p4, p2)

  // General case: the two segments straddle each other.
  if (((o1 > 0) !== (o2 > 0)) && ((o3 > 0) !== (o4 > 0)) &&
      o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0) {
    return true
  }

  // Collinear special cases: a shared/overlapping span counts as an intersection.
  if (o1 === 0 && onSegment(p1, p3, p2)) return true
  if (o2 === 0 && onSegment(p1, p4, p2)) return true
  if (o3 === 0 && onSegment(p3, p1, p4)) return true
  if (o4 === 0 && onSegment(p3, p2, p4)) return true

  return false
}

/**
 * Returns true when the closed polygon described by `points` crosses itself:
 * any two NON-ADJACENT edges intersect. Edges that share a vertex (adjacent
 * edges, including the wrap-around pair first<->last) do not count. The closing
 * edge (last -> first) is included in the check.
 *
 * Fewer than 4 points can never self-intersect (a triangle is always simple),
 * so the function returns false for < 4 points.
 */
export function polygonSelfIntersects(points: Point2D[]): boolean {
  if (!Array.isArray(points) || points.length < 4) return false

  const n = points.length
  // Build the closed edge list: edge i goes from points[i] to points[(i+1)%n].
  for (let i = 0; i < n; i++) {
    const a1 = points[i]
    const a2 = points[(i + 1) % n]

    // Compare edge i against every later edge j, skipping adjacent edges
    // (those sharing a vertex). Adjacent pairs: j === i+1, and the wrap-around
    // pair where i === 0 and j === n-1 (edge n-1 -> 0 touches edge 0 -> 1).
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue // wrap-around adjacency

      const b1 = points[j]
      const b2 = points[(j + 1) % n]

      if (segmentsIntersect(a1, a2, b1, b2)) return true
    }
  }

  return false
}

export interface LawnSizeResult {
  totalAreaSqFt: number
  totalAreaAcres: number
  // Perimeter / edging length in whole feet (0 when unknown, e.g. shared links).
  totalPerimeterFt: number
  measurementMethod: string
  // Total vertex count across all traced lawn-area polygons (map_draw only).
  // Absent for manual paths — confidence cue is not shown when undefined.
  vertexCount?: number

  mowerDeckSize: {
    recommended: string
    options: MowerOption[]
  }

  spreaderCapacity: {
    recommended: string
    capacity: string
    refillCount: number
    priceRange: string
  }

  productQuantities: {
    grassSeed: {
      lbs: number
      bags: number
      rateLbsPer1000SqFt: number
      bagSizeLbs: number
    }
    fertilizer: {
      lbs: number
      bags: number
      rateLbsPer1000SqFt: number
      bagSizeLbs: number
    }
    mulch: {
      cubicYards: number
    }
  }

  sizeTips: string[]
  budgetEstimate: {
    annual: string
    range: string
  }
}

export function calculateLawnSize(input: LawnSizeInput): LawnSizeResult {
  const sqFt = input.totalAreaSqFt
  const acres = sqFt / 43560

  const mowerRec = getMowerRecommendation(sqFt)
  const spreaderRec = getSpreaderRecommendation(sqFt)
  const productQuantities = calculateProductQuantities(sqFt)
  const sizeTips = generateSizeTips(sqFt)
  const budgetEstimate = calculateBudgetEstimate(sqFt)

  return {
    totalAreaSqFt: Math.round(sqFt),
    totalAreaAcres: parseFloat(acres.toFixed(3)),
    totalPerimeterFt: Math.round(input.totalPerimeterFt ?? 0),
    measurementMethod: input.measurementMethod,
    vertexCount: input.vertexCount,
    mowerDeckSize: {
      recommended: mowerRec.recommended,
      options: mowerRec.options
    },
    spreaderCapacity: {
      recommended: spreaderRec.type,
      capacity: spreaderRec.capacity,
      refillCount: spreaderRec.refillsNeeded,
      priceRange: spreaderRec.priceRange
    },
    productQuantities,
    sizeTips,
    budgetEstimate
  }
}

function calculateProductQuantities(sqFt: number) {
  const grassSeedLbsPer1000 = LAWN_SIZE_RATE_ASSUMPTIONS.grassSeedLbsPer1000SqFt
  const fertilizerLbsPer1000 = LAWN_SIZE_RATE_ASSUMPTIONS.fertilizerProductLbsPer1000SqFt
  const mulchDepthInches = 2

  const grassSeedLbs = Math.round((sqFt / 1000) * grassSeedLbsPer1000)
  const fertilizerLbs = Math.round((sqFt / 1000) * fertilizerLbsPer1000)
  const mulchCubicYards = parseFloat(((sqFt * (mulchDepthInches / 12)) / 27).toFixed(1))

  return {
    grassSeed: {
      lbs: grassSeedLbs,
      bags: Math.ceil(grassSeedLbs / LAWN_SIZE_RATE_ASSUMPTIONS.grassSeedBagSizeLbs),
      rateLbsPer1000SqFt: grassSeedLbsPer1000,
      bagSizeLbs: LAWN_SIZE_RATE_ASSUMPTIONS.grassSeedBagSizeLbs,
    },
    fertilizer: {
      lbs: fertilizerLbs,
      bags: Math.ceil(fertilizerLbs / LAWN_SIZE_RATE_ASSUMPTIONS.fertilizerBagSizeLbs),
      rateLbsPer1000SqFt: fertilizerLbsPer1000,
      bagSizeLbs: LAWN_SIZE_RATE_ASSUMPTIONS.fertilizerBagSizeLbs,
    },
    mulch: {
      cubicYards: mulchCubicYards
    }
  }
}

function generateSizeTips(sqFt: number): string[] {
  const tips: string[] = []

  if (sqFt < 5000) {
    tips.push('Your lawn is small enough for manual maintenance with basic equipment')
    tips.push('Consider a reel mower for exercise, quiet operation, and a cleaner cut')
    tips.push('Hand tools and manual spreaders will work perfectly for this size')
    tips.push('Watering can be managed with a single sprinkler zone')
    tips.push('Fertilizing and seeding will take minimal time and product')
  } else if (sqFt < 10000) {
    tips.push('Your lawn is medium-sized - a good balance of manual and power equipment')
    tips.push('A self-propelled mower saves effort while staying easy to maneuver around beds and gates')
    tips.push('Consider investing in a quality broadcast spreader for even coverage')
    tips.push('Plan for 2-3 sprinkler zones for efficient watering')
    tips.push('Budget about 30-45 minutes per week for mowing during growing season')
  } else if (sqFt < 20000) {
    tips.push('Your lawn is large - quality power equipment is a worthwhile investment')
    tips.push('A riding mower can reduce mowing time if the lawn has enough open, level space')
    tips.push('Consider a tow-behind spreader to reduce application time')
    tips.push('Plan for a multi-zone irrigation system for best coverage')
    tips.push('Budget 45-60 minutes per week for mowing during peak growing season')
  } else {
    tips.push('You have a very large lawn - professional-grade equipment recommended')
    tips.push('A zero-turn mower will provide the best efficiency and cut quality')
    tips.push('Consider hiring professionals for fertilization and weed control')
    tips.push('A comprehensive irrigation system with smart controls is highly beneficial')
    tips.push('Budget 60-90+ minutes per week for mowing, or consider professional service')
  }

  tips.push(`For ${Math.round(sqFt).toLocaleString()} square feet of lawn (${(sqFt / 43560).toFixed(2)} acres), the planning estimate is ${Math.round((sqFt / 1000) * LAWN_SIZE_RATE_ASSUMPTIONS.grassSeedLbsPer1000SqFt)} lbs of grass seed at ${LAWN_SIZE_RATE_ASSUMPTIONS.grassSeedLbsPer1000SqFt} lb per 1,000 sq ft, and ${Math.round((sqFt / 1000) * LAWN_SIZE_RATE_ASSUMPTIONS.fertilizerProductLbsPer1000SqFt)} lbs of fertilizer product at ${LAWN_SIZE_RATE_ASSUMPTIONS.fertilizerProductLbsPer1000SqFt} lb per 1,000 sq ft. Actual rates depend on grass type and the product label.`)

  return tips
}

function calculateBudgetEstimate(sqFt: number): { annual: string; range: string } {
  let annualMin: number
  let annualMax: number

  if (sqFt < 3000) {
    annualMin = 50
    annualMax = 100
  } else if (sqFt < 8000) {
    annualMin = 150
    annualMax = 300
  } else if (sqFt < 15000) {
    annualMin = 300
    annualMax = 600
  } else {
    annualMin = 600
    annualMax = 1200
  }

  const baseCost = (sqFt / 1000) * 40
  annualMin = Math.max(annualMin, baseCost * 0.8)
  annualMax = Math.max(annualMax, baseCost * 1.5)

  return {
    annual: `$${Math.round(annualMin)}-$${Math.round(annualMax)}`,
    range: sqFt < 3000 ? 'Small Budget' : sqFt < 8000 ? 'Medium Budget' : sqFt < 15000 ? 'Large Budget' : 'Premium Budget'
  }
}

/** Planning bounds for a lawn this tool will analyze. Above the max the
 *  measurement is still real (large properties exist) — only the equipment,
 *  quantity, and budget models stop being meaningful. */
export const MIN_LAWN_SIZE_SQ_FT = 100
export const MAX_LAWN_SIZE_SQ_FT = 217800 // 5 acres

export function validateLawnSize(sqFt: number): { valid: boolean; error?: string } {
  if (!Number.isFinite(sqFt)) {
    return {
      valid: false,
      error: 'Enter a lawn size using finite numbers, then try again.'
    }
  }

  if (sqFt < MIN_LAWN_SIZE_SQ_FT) {
    return {
      valid: false,
      error: 'Lawn size must be at least 100 square feet. Please check your measurements.'
    }
  }

  if (sqFt > MAX_LAWN_SIZE_SQ_FT) {
    return {
      valid: false,
      error: 'Lawn size exceeds 5 acres (217,800 sq ft). For properties this large, consider consulting a professional landscaping service for accurate estimates.'
    }
  }

  return { valid: true }
}

export function calculateManualRectangle(length: number, width: number): number {
  return length * width
}

export function calculateManualIrregular(areas: Array<{ length: number; width: number }>): number {
  return areas.reduce((total, area) => total + (area.length * area.width), 0)
}

// ============================================================================
// Shareable lawn-trace codec
// ----------------------------------------------------------------------------
// Encodes the traced polygon(s) into a compact, URL-safe string so a
// measurement can be shared, re-opened, and refined. The web app's deep link
// historically only carried `?size=` (a bare number); `?lawn=` carries the
// actual outline. Pure functions, NO google.maps dependency, so they are
// unit-testable in jest. decodeLawnTrace is tolerant: it returns empty on any
// malformed input and never throws (a hand-edited URL must not crash the tool).
// ============================================================================

export interface LatLng {
  lat: number
  lng: number
}

export interface DecodedLawnTrace {
  areas: LatLng[][]
  exclusions: LatLng[][]
}

interface EncodableRing {
  coordinates: LatLng[]
}

// Coordinate precision. 6 decimals ~= 0.11m at the equator, plenty for a yard
// outline, and keeps the string short.
const TRACE_COORD_DECIMALS = 6

// Format version prefix. Bump if the wire format changes incompatibly.
const TRACE_VERSION = '1'

// Section separator between the areas list and the exclusions list.
const SECTION_SEP = '~'
// Separator between polygons within a section.
const POLY_SEP = '!'
// Separator between points within a polygon.
const POINT_SEP = '_'

const roundCoord = (n: number): number =>
  Math.round(n * 10 ** TRACE_COORD_DECIMALS) / 10 ** TRACE_COORD_DECIMALS

// A single point becomes "lat:lng" with rounded fixed precision. We use ':' as
// the lat/lng delimiter so the value itself can keep its decimal point and an
// optional leading minus sign without ambiguity.
const encodePoint = (p: LatLng): string =>
  `${roundCoord(p.lat)}:${roundCoord(p.lng)}`

const encodeRing = (ring: LatLng[]): string =>
  ring.map(encodePoint).join(POINT_SEP)

const encodeSection = (rings: LatLng[][]): string =>
  rings.map(encodeRing).join(POLY_SEP)

/**
 * Encode traced lawn areas + exclusions into a compact, URL-safe string.
 * Coordinates are rounded to ~6 decimals. Areas and exclusions live in two
 * distinguishable sections separated by `~`. Returns '' when there is nothing
 * to encode.
 */
export function encodeLawnTrace(
  areas: EncodableRing[],
  exclusions: EncodableRing[],
): string {
  const areaRings = (areas ?? [])
    .map((a) => a?.coordinates ?? [])
    .filter((coords) => coords.length > 0)
  const exclusionRings = (exclusions ?? [])
    .map((e) => e?.coordinates ?? [])
    .filter((coords) => coords.length > 0)

  if (areaRings.length === 0 && exclusionRings.length === 0) return ''

  const body = `${encodeSection(areaRings)}${SECTION_SEP}${encodeSection(exclusionRings)}`
  return `${TRACE_VERSION}${SECTION_SEP}${body}`
}

const isValidCoord = (n: number, max: number): boolean =>
  Number.isFinite(n) && Math.abs(n) <= max

const decodePoint = (token: string): LatLng | null => {
  const parts = token.split(':')
  if (parts.length !== 2) return null
  const lat = Number(parts[0])
  const lng = Number(parts[1])
  if (!isValidCoord(lat, 90) || !isValidCoord(lng, 180)) return null
  return { lat, lng }
}

const decodeRing = (token: string): LatLng[] => {
  if (!token) return []
  const ring: LatLng[] = []
  for (const pointToken of token.split(POINT_SEP)) {
    const point = decodePoint(pointToken)
    if (point) ring.push(point)
  }
  return ring
}

const decodeSection = (token: string): LatLng[][] => {
  if (!token) return []
  return token
    .split(POLY_SEP)
    .map(decodeRing)
    // Drop degenerate rings (a polygon needs at least 3 points).
    .filter((ring) => ring.length >= 3)
}

/**
 * Inverse of encodeLawnTrace. Tolerant of malformed input: returns
 * { areas: [], exclusions: [] } on any parse failure and never throws.
 */
export function decodeLawnTrace(s: string): DecodedLawnTrace {
  const empty: DecodedLawnTrace = { areas: [], exclusions: [] }
  if (typeof s !== 'string' || s.length === 0) return empty

  try {
    const sections = s.split(SECTION_SEP)
    // Expect: version, areas, exclusions. Be lenient about extra trailing parts.
    if (sections.length < 3) return empty
    if (sections[0] !== TRACE_VERSION) return empty

    const areas = decodeSection(sections[1])
    const exclusions = decodeSection(sections[2])
    return { areas, exclusions }
  } catch {
    return empty
  }
}
